/* =========================================
   WAMBOLT TABLE OF CONTENTS
   ========================================= */

(function () {
  const ROOT_SELECTOR = '[data-toc-wrap]';

  const instances = new Map();
  const skippedHeadings = new WeakSet();

  function getScrollTrigger() {
    return window.ScrollTrigger || null;
  }

  function getScroller() {
    const scrollElement =
      window.wamboltScrollElement;

    if (
      scrollElement &&
      scrollElement !== window &&
      scrollElement instanceof HTMLElement
    ) {
      return scrollElement;
    }

    return window;
  }

  function getOffset(root) {
    const explicitOffset =
      Number.parseFloat(
        root.getAttribute(
          'data-toc-offset'
        )
      );

    if (
      Number.isFinite(
        explicitOffset
      )
    ) {
      return explicitOffset;
    }

    const rootStyles =
      getComputedStyle(
        document.documentElement
      );

    const headerClearance =
      Number.parseFloat(
        rootStyles.getPropertyValue(
          '--w-header-clearance'
        )
      );

    return Number.isFinite(
      headerClearance
    )
      ? headerClearance
      : 80;
  }

  function getHeadingLevels(root) {
    return (
      root.getAttribute(
        'data-toc-levels'
      ) ||
      'h2,h3'
    )
      .split(',')
      .map((level) =>
        level
          .trim()
          .toLowerCase()
      )
      .filter((level) =>
        /^h[1-6]$/.test(level)
      );
  }

  function slugify(
    text,
    slugCounts
  ) {
    let slug = text
      .toLowerCase()
      .replace(
        /[^a-z0-9\s-]/g,
        ''
      )
      .replace(
        /\s+/g,
        '-'
      )
      .replace(
        /-+/g,
        '-'
      )
      .replace(
        /^-|-$/g,
        ''
      );

    if (!slug) {
      slug = 'section';
    }

    const count =
      slugCounts.get(slug) ||
      0;

    slugCounts.set(
      slug,
      count + 1
    );

    return count === 0
      ? slug
      : `${slug}-${count + 1}`;
  }

  function stripSkipMarker(
    element,
    marker
  ) {
    const walker =
      document.createTreeWalker(
        element,
        NodeFilter.SHOW_TEXT
      );

    let node;

    while (
      (node =
        walker.nextNode())
    ) {
      if (
        node.textContent.includes(
          marker
        )
      ) {
        node.textContent =
          node.textContent
            .replace(
              marker,
              ''
            )
            .trim();
      }
    }
  }

  function getMobileActiveIndex(
    headings,
    index
  ) {
    if (
      !window
        .matchMedia(
          '(max-width: 989px)'
        )
        .matches
    ) {
      return index;
    }

    const currentHeading =
      headings[index];

    if (
      !currentHeading ||
      currentHeading.tagName !==
        'H3'
    ) {
      return index;
    }

    for (
      let i = index - 1;
      i >= 0;
      i -= 1
    ) {
      if (
        headings[i].tagName ===
        'H2'
      ) {
        return i;
      }
    }

    return index;
  }

  function scrollToTarget(
    target,
    offset
  ) {
    const reducedMotion =
      window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;

    const lenis =
      window.wamboltLenis;

    if (
      lenis &&
      typeof lenis.scrollTo ===
        'function'
    ) {
      lenis.scrollTo(
        target,
        {
          offset: -offset,
          immediate:
            reducedMotion,
        }
      );

      return;
    }

    const scroller =
      getScroller();

    if (
      scroller !== window
    ) {
      const scrollerRect =
        scroller
          .getBoundingClientRect();

      const targetRect =
        target
          .getBoundingClientRect();

      const top =
        targetRect.top -
        scrollerRect.top +
        scroller.scrollTop -
        offset;

      scroller.scrollTo({
        top,
        behavior:
          reducedMotion
            ? 'auto'
            : 'smooth',
      });

      return;
    }

    const top =
      target
        .getBoundingClientRect()
        .top +
      window.scrollY -
      offset;

    window.scrollTo({
      top,
      behavior:
        reducedMotion
          ? 'auto'
          : 'smooth',
    });
  }

  function destroyRoot(root) {
    const instance =
      instances.get(root);

    if (!instance) {
      return;
    }

    instance.scrollTriggers
      .forEach((trigger) => {
        trigger.kill();
      });

    instance.listElement
      .removeEventListener(
        'click',
        instance.onClick
      );

    instance.generatedLinks
      .forEach((link) => {
        link.remove();
      });

    instance.generatedIds
      .forEach((heading) => {
        heading.removeAttribute(
          'id'
        );

        heading.removeAttribute(
          'data-wambolt-toc-generated-id'
        );
      });

    instances.delete(root);
  }

  function initRoot(root) {
    if (
      instances.has(root)
    ) {
      return;
    }

    const contentElement =
      root.querySelector(
        '[data-toc-content]'
      );

    const listElement =
      root.querySelector(
        '[data-toc-list]'
      );

    const templateElement =
      root.querySelector(
        '[data-toc-template]'
      );

    const templateLink =
      templateElement
        ?.content
        ?.querySelector(
          '[data-toc-link]'
        );

    if (
      !contentElement ||
      !listElement ||
      !templateElement ||
      !templateLink
    ) {
      return;
    }

    listElement
      .querySelectorAll(
        '[data-toc-generated]'
      )
      .forEach((link) =>
        link.remove()
      );

    const levels =
      getHeadingLevels(root);

    const levelSelector =
      levels.join(', ');

    if (!levelSelector) {
      root.setAttribute(
        'data-toc-empty',
        'true'
      );

      return;
    }

    const skipMarker =
      '{skip}';

    const slugCounts =
      new Map();

    const generatedIds = [];

    const headings =
      Array.from(
        contentElement
          .querySelectorAll(
            levelSelector
          )
      ).filter(
        (heading) => {
          if (
            heading.hasAttribute(
              'data-toc-ignore'
            ) ||
            skippedHeadings.has(
              heading
            )
          ) {
            return false;
          }

          if (
            heading.textContent
              .includes(
                skipMarker
              )
          ) {
            stripSkipMarker(
              heading,
              skipMarker
            );

            skippedHeadings.add(
              heading
            );

            return false;
          }

          return Boolean(
            heading.textContent
              .trim()
          );
        }
      );

    if (!headings.length) {
      root.setAttribute(
        'data-toc-empty',
        'true'
      );

      return;
    }

    root.removeAttribute(
      'data-toc-empty'
    );

    headings.forEach(
      (heading) => {
        if (!heading.id) {
          heading.id =
            slugify(
              heading
                .textContent
                .trim(),
              slugCounts
            );

          heading.setAttribute(
            'data-wambolt-toc-generated-id',
            'true'
          );

          generatedIds.push(
            heading
          );
        }
      }
    );

    const generatedLinks =
      headings.map(
        (heading) => {
          const fragment =
            templateElement
              .content
              .cloneNode(true);

          const link =
            fragment
              .querySelector(
                '[data-toc-link]'
              );

          const textTarget =
            link.querySelector(
              '[data-toc-text]'
            ) ||
            link;

          const level =
            heading.tagName
              .charAt(1);

          textTarget.textContent =
            heading
              .textContent
              .trim();

          link.href =
            `#${heading.id}`;

          link.removeAttribute(
            'data-toc-link'
          );

          link.setAttribute(
            'data-toc-item',
            ''
          );

          link.setAttribute(
            'data-toc-generated',
            ''
          );

          link.setAttribute(
            'data-toc-depth',
            level
          );

          link.setAttribute(
            'data-toc-status',
            ''
          );

          listElement
            .appendChild(
              fragment
            );

          return (
            listElement
              .lastElementChild
          );
        }
      );

    function setActive(
      index
    ) {
      const activeIndex =
        getMobileActiveIndex(
          headings,
          index
        );

      generatedLinks
        .forEach((link) => {
          link.setAttribute(
            'data-toc-status',
            ''
          );

          link.removeAttribute(
            'aria-current'
          );
        });

      const activeLink =
        generatedLinks[
          activeIndex
        ];

      if (activeLink) {
        activeLink.setAttribute(
          'data-toc-status',
          'active'
        );

        activeLink.setAttribute(
          'aria-current',
          'location'
        );
      }
    }

    setActive(0);

    const offset =
      getOffset(root);

    const ScrollTrigger =
      getScrollTrigger();

    const scrollTriggers = [];

    if (ScrollTrigger) {
      const scroller =
        getScroller();

      headings.forEach(
        (
          heading,
          index
        ) => {
          const nextHeading =
            headings[
              index + 1
            ];

          const trigger =
            ScrollTrigger.create(
              {
                trigger:
                  heading,

                scroller:
                  scroller ===
                  window
                    ? undefined
                    : scroller,

                start:
                  `top ${
                    offset + 1
                  }px`,

                endTrigger:
                  nextHeading ||
                  contentElement,

                end:
                  nextHeading
                    ? `top ${
                        offset +
                        1
                      }px`
                    : 'bottom top',

                invalidateOnRefresh:
                  true,

                onToggle(
                  self
                ) {
                  if (
                    self.isActive
                  ) {
                    setActive(
                      index
                    );
                  }
                },
              }
            );

          scrollTriggers.push(
            trigger
          );
        }
      );
    }

    function onClick(
      event
    ) {
      const link =
        event.target.closest(
          '[data-toc-item]'
        );

      if (
        !link ||
        !listElement
          .contains(link)
      ) {
        return;
      }

      event.preventDefault();

      const id =
        link
          .getAttribute(
            'href'
          )
          ?.slice(1);

      const target =
        id
          ? document
              .getElementById(
                id
              )
          : null;

      if (!target) {
        return;
      }

      scrollToTarget(
        target,
        getOffset(root)
      );
    }

    listElement
      .addEventListener(
        'click',
        onClick
      );

    instances.set(
      root,
      {
        listElement,
        generatedLinks,
        generatedIds,
        scrollTriggers,
        onClick,
      }
    );
  }

  function initAll(
    scope = document
  ) {
    if (
      scope.matches?.(
        ROOT_SELECTOR
      )
    ) {
      initRoot(scope);
    }

    scope
      .querySelectorAll?.(
        ROOT_SELECTOR
      )
      .forEach(initRoot);
  }

  function destroyWithin(
    scope = document
  ) {
    Array.from(
      instances.keys()
    ).forEach((root) => {
      if (
        scope === document ||
        scope === root ||
        scope.contains?.(
          root
        )
      ) {
        destroyRoot(root);
      }
    });
  }

  function rebuildAll() {
    const roots =
      Array.from(
        instances.keys()
      );

    roots.forEach(
      destroyRoot
    );

    roots.forEach(
      initRoot
    );

    const ScrollTrigger =
      getScrollTrigger();

    if (
      ScrollTrigger?.sort
    ) {
      ScrollTrigger.sort();
    }

    if (
      ScrollTrigger?.refresh
    ) {
      requestAnimationFrame(
        () => {
          requestAnimationFrame(
            () => {
              ScrollTrigger
                .refresh();
            }
          );
        }
      );
    }
  }

  function boot() {
    initAll(document);
  }

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      boot,
      {
        once: true,
      }
    );
  } else {
    boot();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initAll(
        event.target
      );
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      destroyWithin(
        event.target
      );
    }
  );

  document.addEventListener(
    'wambolt:page:before-leave',
    () => {
      destroyWithin(
        document
      );
    }
  );

  document.addEventListener(
    'wambolt:page:enter',
    () => {
      initAll(
        document
      );
    }
  );

  document.addEventListener(
    'wambolt:lenis:ready',
    () => {
      rebuildAll();
    }
  );
})();